import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
/**
 * `Person` — une personne physique **ou morale** intervenant dans une demande (M15).
 *
 * Cinq rôles s'y rattachent, portés par des clés étrangères sur les entités concernées :
 * propriétaire et occupant sur `Site`, demandeur payeur et contact de rendez-vous sur
 * `Inquiry`, personne rencontrée sur `Inspection`. La table, elle, ne connaît pas les rôles —
 * une même personne peut en tenir plusieurs, et sur plusieurs demandes.
 *
 * ⚠️ **Aucun champ n'est exigé par la ligne, et ce n'est pas de la négligence** (M24).
 *
 * - `firstName` est absent d'une **personne morale** : dans l'exemple réel de M15, le
 *   propriétaire est la SCI `AM PATRIMOINE` et le contact de rendez-vous l'agence
 *   `BLANDINEAU IMMOBILIER`. Exiger un prénom obligerait à en inventer un.
 * - `phone` et `email` viennent d'imports client et de formulaires : ils manquent
 *   régulièrement. Les refuser ferait rejeter la demande entière alors qu'on sait qui elle
 *   concerne — exactement ce que M21 interdit. On enregistre, on signale, l'humain tranche.
 * - `lastName` manque quand on note **un téléphone sans nom** — l'agent en prenant un rendez-vous
 *   sur place (#268), le bureau à la création ou à l'import (#379) : le numéro devient la personne
 *   comme une autre, et l'écran n'affiche pas de nom (`displayName` rend `null`).
 *
 * Ce que ça implique : **une personne peut n'être joignable par aucun canal, ni nommable**. Tout
 * code qui veut la contacter ou la nommer doit le vérifier ; c'est un cas de gestion, pas un cas
 * impossible. ⚠️ Et « la jointure a trouvé sa personne » se lit sur son **identifiant**, jamais
 * sur son nom.
 *
 * ⚠️ **`address` et `municipality` sont du texte, pas l'`address_id` du diagramme** (#350, décision
 * du 05/10/2026). Le rapport papier imprime l'adresse du **propriétaire**, qui n'habite pas toujours
 * le lieu contrôlé ; elle arrive d'un formulaire ou d'une tablette, sans code INSEE à rattacher.
 * Verbatim, et rien ne la redécoupe (M1, M32). Seul le propriétaire les remplit aujourd'hui.
 *
 * ⚠️ Et pas d'index sur `lastName` : la recherche libre de M14 ne porte pas sur les personnes
 * tant que Q10 n'a pas répondu. Si elle y vient, c'est par **sous-chaîne** — donc un GIN
 * `pg_trgm`, jamais le btree que `drizzle-kit generate` pose par défaut, qu'un `ILIKE '%…%'`
 * n'ouvrirait pas.
 */
/** Les adresses d'un champ e-mail, séparées par « ; » ou « , » comme dans Outlook. */
export function emailAddresses(value) {
    return value
        .split(/[;,]/)
        .map((address) => address.trim())
        .filter((address) => address !== '');
}
const singleEmailSchema = z.email();
/**
 * Le champ e-mail d'une personne : **une ou plusieurs adresses** (« a@x.fr; b@y.fr »), parce que
 * le bureau écrit parfois à deux personnes pour un même rendez-vous (Francis, 30/10/2026). Le
 * champ reste une seule chaîne ; seul l'envoi le découpe (`emailAddresses`).
 */
export const personEmailSchema = z
    .string()
    .trim()
    .refine((value) => {
    const addresses = emailAddresses(value);
    return addresses.length > 0 && addresses.every((a) => singleEmailSchema.safeParse(a).success);
});
export const personSchema = z.object({
    id: uuidV7Schema,
    /** Nom de famille, ou raison sociale — c'est la même colonne (`Nom Dem` / `Raison Dem`). */
    lastName: z.string().trim().min(1).nullable(),
    firstName: z.string().trim().min(1).nullable(),
    email: personEmailSchema.nullable(),
    phone: z.string().trim().min(1).nullable(),
    /** L'adresse postale telle qu'écrite (« 4 rue des Lilas ») — sans commune ni code postal. */
    address: z.string().trim().min(1).nullable(),
    /** La commune telle qu'écrite, code postal compris s'il a été donné (« 44000 Nantes »). */
    municipality: z.string().trim().min(1).nullable(),
});
/**
 * L'**avertissement** d'une adresse e-mail qui n'en est pas une — « tel uniquement », une faute
 * de frappe —, ou `null` quand elle est utilisable ou absente.
 *
 * C'est la forme que M21 donne à un avertissement : **dérivé, pas stocké**, recalculé à
 * l'affichage par la même fonction partout. La tablette l'affiche sous le champ au lieu de
 * refuser la saisie : l'agent est seul devant l'écran, et la personne en face de lui dicte peut-
 * être mal — le bureau rappellera. Aucun envoi ne part vers une adresse hors format
 * (`usableEmail`, côté API).
 *
 * ⚠️ **Le message ne nomme pas d'envoi**, et c'est voulu : il s'affiche sous plusieurs personnes,
 * et seul l'écran sait laquelle reçoit quoi — la confirmation part à la personne présente
 * (`confirmationRecipientOf`), l'avis de passage encore au demandeur.
 */
export function emailWarning(email) {
    const typed = email?.trim() ?? '';
    if (typed === '' || personSchema.shape.email.safeParse(typed).success)
        return null;
    return 'Adresse e-mail hors format : elle ne pourra servir à aucun envoi.';
}
/**
 * L'**avertissement** d'un téléphone qui n'a pas dix chiffres (Sentry TERRAIN-7), ou `null`.
 * Même forme que `emailWarning` (M21) : le numéro s'enregistre tel que saisi, le champ le signale.
 * Espaces, points et tirets ne comptent pas, et « +33 » ou « 0033 » vaut le « 0 » — l'écriture
 * que `mobileNumberOf` lit déjà.
 */
export function phoneWarning(phone) {
    const digits = phone?.replaceAll(/[\s.-]/g, '') ?? '';
    if (digits === '' || /^(?:0|\+33|0033)\d{9}$/.test(digits))
        return null;
    return 'Numéro hors format : 10 chiffres attendus.';
}
export function displayName(person) {
    const parts = [person.firstName, person.lastName].filter((part) => part !== null);
    return parts.length === 0 ? null : parts.join(' ');
}
/**
 * Le nom saisi **désigne-t-il une autre personne** que celle en place ? Un autre nom, oui — casse
 * et espaces mis à part ; aucun nom saisi, ou une personne qui n'en avait pas, non.
 *
 * ⚠️ **Sert à défaire un lien, jamais à en poser un** (#407) : une saisie qui ne connaît pas les
 * liens — la tablette — ne doit pas renommer le propriétaire lié à la personne présente. Deux
 * homonymes ne sont pas pour autant une seule personne (M24).
 */
export function namesAnotherPerson(previous, next) {
    const fold = (name) => name?.trim().toLocaleLowerCase('fr') ?? '';
    const [before, after] = [fold(previous), fold(next)];
    return before !== '' && after !== '' && before !== after;
}
//# sourceMappingURL=person.js.map